import React, { forwardRef, useState, useEffect, useImperativeHandle } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { IconButton, Avatar, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  width: '100%',
  '& .MuiAutocomplete-root': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& *': {
    boxSizing: 'border-box !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#E3E7FE',
    minHeight: '46px !important',
    maxHeight: '46px !important',
    lineHeight: '46px',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#E3E7FE',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
    minHeight: '46px !important',
    maxHeight: '46px !important',
    height: '46px !important',
    boxSizing: 'border-box',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#1222b3',
    fontFamily: 'Montserrat',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  '& .MuiDataGrid-sortIcon': {
    opacity: '0.8 !important',
    color: '#1222b3',
  },
  '& .MuiDataGrid-menuIconButton': {
    color: '#1222b3',
  },
  '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
    flexDirection: 'row',
  },
  '& .MuiDataGrid-main, & .MuiDataGrid-overlayWrapper': {
    backgroundColor: '#ffffff',
  },
  '& .MuiDataGrid-virtualScroller': {
    backgroundColor: '#ffffff',
    overflowX: 'auto',
    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
    scrollbarColor: '#d8daf2 transparent',
    borderRadius: 'inherit',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar, & .MuiDataGrid-scrollbar::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track, & .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb, & .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
    background: '#d8daf2',
    borderRadius: 'var(--xtram-radius-pill, 999px)',
  },
  '& .MuiDataGrid-scrollbar': {
    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
    scrollbarColor: '#d8daf2 transparent',
    borderRadius: 'inherit',
  },
  '& .MuiDataGrid-cell': {
    borderTop: '1px solid rgba(191, 191, 193, 0.56)',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(227, 230, 255, 0.3)',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .disableUser': {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  // Edit mode styles
  '& .MuiDataGrid-cell--editing': {
    padding: 0,
    backgroundColor: 'inherit',
  },
  '& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
    borderRight: 'none',
  },
  '& .MuiDataGrid-row--editing .MuiDataGrid-cell:not(:last-child)': {
    borderRight: '8px solid transparent',
  },
}));

const ClientItemDataGrid = forwardRef((props, ref) => {
  const { data, direction, onEdit, onDelete, onSelectRows } = props;
  const [selectedRows, setSelectedRows] = useState([]);
  const [rows, setRows] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    setRows(data || []);
  }, [data]);

  // Let the parent unselect rows programmatically if needed
  useImperativeHandle(ref, () => ({
    deselectRows(ids) {
      setSelectedRows((prev) => prev.filter((rowId) => !ids.includes(rowId)));
    },
  }));

  const handleRowSelectionModelChange = (selection) => {
    setSelectedRows(selection);
    if (onSelectRows) {
      onSelectRows(selection);
    }
  };

  const columns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      // Typically we don't need sorting for action buttons
      sortable: false,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <IconButton>
              <EditIcon style={{ color: '#304FFF' }} />
            </IconButton>
          }
          label="Edit"
          onClick={() => onEdit(params.row)}
          showInMenu={false}
        />,
        <GridActionsCellItem
          icon={
            <IconButton>
              <DeleteIcon style={{ color: 'red' }} />
            </IconButton>
          }
          label="Delete"
          onClick={() => onDelete(params.row)}
          showInMenu={false}
        />,
      ],
    },
    {
      field: 'imageUrl',
      headerName: t('Photo'),
      flex: 0.8,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        if (params.value) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Avatar
                src={params.value}
                variant="rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(params.value, '_blank');
                }}
                sx={{
                  width: 40,
                  height: 40,
                  border: '2px solid #304FFF',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              />
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 40,
                height: 40,
                backgroundColor: '#f5f5f5',
                color: '#999',
                fontSize: '12px',
              }}
            >
              {t('NoImage')}
            </Avatar>
          </Box>
        );
      },
    },
    {
      field: 'price',
      headerName: t('Price'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      sortable: true,
      // MUI X DataGrid v6+: valueGetter(value, row)
      valueGetter: (value, row) => {
        if (!row) return Number(value) || 0;
        const prices = row.prices;
        if (Array.isArray(prices) && prices.length > 0) {
          const def = prices.find((p) => p.isDefault) || prices[0];
          const amount = def?.amount ?? row.price ?? value;
          return amount != null && amount !== '' ? Number(amount) : 0;
        }
        return Number(row.price ?? value ?? 0);
      },
      renderCell: (params) => {
        const row = params?.row;
        const prices = row?.prices;
        const extra = Array.isArray(prices) && prices.length > 1 ? ` (+${prices.length - 1})` : '';
        const display =
          params.value != null && params.value !== ''
            ? params.value
            : (Array.isArray(prices) && prices.length > 0
                ? (prices.find((p) => p.isDefault) || prices[0])?.amount
                : row?.price) ?? 0;
        return `${beautifyNumber(display || 0)}${extra}`;
      },
    },
    {
      field: 'includeVat',
      headerName: t('VAT'),
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      type: 'boolean',
    
      // Cell UI
      renderCell: (params) => {
        const v = params?.value;
        const label =
          v === true
            ? t('ItemWithVat')
            : v === false
            ? t('ItemWithoutVat')
            : '';
    
        const color =
          v === true ? '#4CAF50' : v === false ? '#F44336' : 'inherit';
    
        return (
          <span style={{ color, fontWeight: 'bold', fontSize: 12 }}>
            {label}
          </span>
        );
      },
    },    
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true, // Ensure sorting is enabled
      renderCell: (params) => (
        <span
          style={{
            whiteSpace: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            wordBreak: 'break-word',
            maxWidth: '100%',
            maxHeight: '42px',
            lineHeight: '21px',
          }}
        >
          {params.value?.trim() || ''}
        </span>
      ),
    },
    {
      field: 'makat',
      headerName: t('Makat'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true, // Ensure sorting is enabled
    },
  ];

  // Reverse columns if needed for RTL
  const finalColumns = direction === 'rtl' ? [...columns].reverse() : columns;

  return (
    <div style={{ width: '100%', height: '60vh', direction, marginTop: '10px' }}>
      <StyledDataGrid
        rows={rows}
        columns={finalColumns}
        getRowId={(row) => row.id}
        pagination
        columnHeaderHeight={46}
        // Sorting is enabled by default, columns are set to 'sortable: true' where needed
        onRowDoubleClick={(params) => onEdit(params.row)}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleRowSelectionModelChange}
      />
    </div>
  );
});

export default ClientItemDataGrid;
